import React, { useState } from "react"
import { useSelector } from "react-redux"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterField, FilterSelect } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildAnalyticsUrl } from "../parts/analyticsDates"
import ThresholdsModal from "../parts/ThresholdsModal"

const PAGE_SIZE = 100

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "14px",
	boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
}

const mutedStyle = { color: "var(--dark-text-muted, #8791a1)", fontSize: "12px" }

const inputStyle = { borderRadius: "10px", minHeight: "38px" }

const statusMeta = {
	dead_stock: { label: "Не расходуется", color: "#dc2626", bg: "var(--dark-tint-danger, #fdecec)", icon: "uil-exclamation-triangle" },
	overstock: { label: "Избыток", color: "#e0762c", bg: "var(--dark-tint-warning, #fff4df)", icon: "uil-layer-group" },
	slow_moving: { label: "Медленно уходит", color: "#2563eb", bg: "var(--dark-tint-primary, #eaf2ff)", icon: "uil-clock" },
}

// Подписи статусов — по порогам заведения (по умолчанию запас на 30 / 90 дней)
function statusHint(status, thresholds) {
	const slow = thresholds?.slow_cover_days ?? 30
	const over = thresholds?.overstock_cover_days ?? 90
	if (status === "dead_stock") return "за окно не было ни одного расхода"
	if (status === "overstock") return `запаса больше чем на ${over} дней`
	return `запаса на ${slow}–${over} дней`
}

const thresholdFields = [
	{ key: "slow_cover_days", label: "Медленно уходит — запаса больше чем на, дней", hint: "Позиции с меньшим запасом считаются нормальными и в отчёт не попадают." },
	{ key: "overstock_cover_days", label: "Избыток — запаса больше чем на, дней", hint: "Должно быть больше порога «медленно уходит»." },
]

const statusOptions = Object.entries(statusMeta).map(([id, meta]) => ({ id, name: meta.label }))

const minDayOptions = [
	{ id: 0, name: "Любой" },
	{ id: 7, name: "7+ дней" },
	{ id: 14, name: "14+ дней" },
	{ id: 30, name: "30+ дней" },
	{ id: 60, name: "60+ дней" },
]

const usageDayOptions = [
	{ id: 14, name: "14 дней" },
	{ id: 30, name: "30 дней" },
	{ id: 60, name: "60 дней" },
	{ id: 90, name: "90 дней" },
]

function formatQty(value) {
	const number = Number(value || 0)
	if (Math.abs(number) >= 100) return formatMoney(number, 0)
	return number.toFixed(2).replace(/\.?0+$/, "").replace(".", ",")
}

function formatDate(value) {
	return value ? new Date(String(value).replace(" ", "T")).toLocaleDateString("ru-RU") : ""
}

function KpiCard({ title, value, sublabel, icon, color = "#dc2626" }) {
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body p-3">
				<div className="d-flex justify-content-between align-items-start">
					<div style={{ ...mutedStyle, textTransform: "uppercase", letterSpacing: "0.4px" }}>{title}</div>
					<div className="d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px", borderRadius: "11px", background: `${color}18`, color }}>
						<i className={`uil ${icon}`}></i>
					</div>
				</div>
				<h3 className="mb-0 mt-2" style={{ fontSize: "26px", fontWeight: 800, color: "var(--dark-text-strong, #111827)", letterSpacing: "-0.5px" }}>{value}</h3>
				{sublabel && <div style={{ ...mutedStyle, marginTop: "2px" }}>{sublabel}</div>}
			</div>
		</div>
	)
}

function StatusCards({ statuses = [], thresholds }) {
	return (
		<div className="row g-3 mb-3">
			{statuses.map(group => {
				const meta = statusMeta[group.status]
				if (!meta) return null
				return (
					<div className="col-md-4" key={group.status}>
						<div className="h-100" style={{ ...cardStyle, background: meta.bg, borderColor: `${meta.color}33`, padding: "16px" }}>
							<div className="d-flex justify-content-between align-items-start">
								<div>
									<div style={{ fontWeight: 900, color: meta.color }}>{meta.label}</div>
									<div style={mutedStyle}>{statusHint(group.status, thresholds)}</div>
								</div>
								<i className={`uil ${meta.icon}`} style={{ color: meta.color, fontSize: "20px" }}></i>
							</div>
							<div className="mt-3" style={{ fontSize: "30px", lineHeight: 1, fontWeight: 900, color: meta.color }}>{group.stock_value_short}</div>
							<div style={mutedStyle}>{formatMoney(group.count, 0)} позиций</div>
						</div>
					</div>
				)
			})}
		</div>
	)
}

function FrozenTable({ items = [], total = 0, usageDays, hasMore, onMore }) {
	const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }
	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-center mb-2">
					<div>
						<h5 className="mb-0" style={{ fontWeight: 800 }}>Где лежат деньги</h5>
						<div style={mutedStyle}>Стоимость — по средней себестоимости склада. Блюда, которые списываются по техкарте, не показываются</div>
					</div>
					<span style={{ ...mutedStyle, background: "var(--dark-surface-2, #f1f4f8)", borderRadius: "14px", padding: "4px 10px", color: "var(--dark-text-muted, #5b6472)" }}>
						{formatMoney(items.length, 0)} из {formatMoney(total, 0)}
					</span>
				</div>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>#</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Товар</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Поставщик</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Остаток</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Сумма в остатке</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Дней без расхода</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Расход {usageDays} дн.</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Запас, дней</th>
								<th className="py-3 text-muted fw-semibold text-center" style={headerThStyle}>Статус</th>
							</tr>
						</thead>
						<tbody>
							{items.length === 0 && <tr><td colSpan="9" className="text-muted text-center py-4">Нет замороженных средств по выбранным фильтрам</td></tr>}
							{items.map(item => {
								const meta = statusMeta[item.status] || statusMeta.slow_moving
								return (
									<tr key={item.product_id}>
										<td className="py-3 text-muted" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>{item.rank}</td>
										<td className="py-3" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>
											<div style={{ fontWeight: 800 }}>{item.product_name}</div>
											<div style={mutedStyle}>
												{item.unit_name || "—"} · последний расход {item.last_outflow_at ? formatDate(item.last_outflow_at) : "не найден"}
												{item.warehouses_count > 1 && ` · складов: ${item.warehouses_count}`}
											</div>
										</td>
										<td className="py-3 text-muted" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>{item.supplier_name}</td>
										<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>{formatQty(item.balance)} {item.unit_name}</td>
										<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)", fontWeight: 900 }}>
											{item.stock_value_short}
											{item.has_no_cost && <div style={{ ...mutedStyle, fontWeight: 400 }}>есть склад без себестоимости</div>}
										</td>
										<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>
											{item.days_without_outflow === null ? <span style={mutedStyle}>нет в журнале</span> : formatMoney(item.days_without_outflow, 0)}
										</td>
										<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>{formatQty(item.used_quantity)} {item.unit_name}</td>
										<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>{item.cover_days === null ? "—" : formatMoney(item.cover_days, 0)}</td>
										<td className="py-3 text-center" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>
											<span style={{ background: meta.bg, color: meta.color, borderRadius: "12px", padding: "4px 10px", fontWeight: 900, whiteSpace: "nowrap" }}>{meta.label}</span>
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
				{hasMore && (
					<div className="text-center pt-3">
						<button className="btn btn-sm btn-link text-decoration-none" onClick={onMore}>
							Показать ещё ({formatMoney(total - items.length, 0)}) →
						</button>
					</div>
				)}
			</div>
		</div>
	)
}

function FrozenFunds() {
	const reduxAccount = useSelector(state => state.account)
	const [data, setData] = useState(null)
	const [items, setItems] = useState([])
	const [showThresholds, setShowThresholds] = useState(false)

	const filters = useAnalyticsFilters({
		pos_id: reduxAccount.pos_id ?? -1,
		wms_id: reduxAccount.wms_id ?? -1,
		category_id: -1,
		supplier_id: -1,
		statuses: [],
		min_days: 0,
		min_amount: "",
		usage_days: 30,
	}, searchWithFilter)
	const { draft, applied, patch } = filters
	const options = useAnalyticsOptions(["warehouses", "suppliers", "categories"], draft.pos_id)

	async function load(filter, page) {
		const response = await GET(buildAnalyticsUrl("/analytics/frozen-funds", filter, { page, limit: PAGE_SIZE }), {}, { loader: true })
		return httpOk(response) ? response.data : null
	}

	async function searchWithFilter(filter) {
		const result = await load(filter, 1)
		if (result) {
			setData(result)
			setItems(result.items || [])
		}
	}

	// Следующая страница — с тем фильтром, по которому построен отчёт, а не с черновиком
	async function loadMore() {
		const result = await load(applied, (data?.page || 1) + 1)
		if (result) {
			setData(result)
			setItems(prev => [...prev, ...(result.items || [])])
		}
	}

	const summary = data?.summary || {}

	return (
		<>
			<AnalyticsFilterBar
				title="Замороженные средства"
				icon="uil-snowflake"
				subtitle="Деньги в остатках, которые не расходуются или уходят слишком медленно"
				filters={filters}
				presets={false}
				actions={
					<button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: "10px", fontWeight: 600 }} onClick={() => setShowThresholds(true)}>
						<i className="uil uil-sliders-v-alt me-1"></i>
						Пороги
					</button>
				}
			>
				<FilterField label="Филиал">
					<FilterSelect options={options.poses} allLabel="Все филиалы" value={draft.pos_id} onChange={(id) => patch({ pos_id: id, wms_id: -1, supplier_id: -1 })} />
				</FilterField>
				<FilterField label="Склад">
					<FilterSelect options={options.warehouses} allLabel="Все склады" value={draft.wms_id} onChange={(id) => patch({ wms_id: id })} />
				</FilterField>
				<FilterField label="Категория">
					<FilterSelect options={options.categories} allLabel="Все категории" value={draft.category_id} onChange={(id) => patch({ category_id: id })} />
				</FilterField>
				<FilterField label="Поставщик">
					<FilterSelect options={options.suppliers} allLabel="Все поставщики" value={draft.supplier_id} onChange={(id) => patch({ supplier_id: id })} />
				</FilterField>
				<FilterField label="Статус">
					<FilterSelect isMulti options={statusOptions} allLabel="Все статусы" value={draft.statuses} onChange={(ids) => patch({ statuses: ids })} />
				</FilterField>
				<FilterField label="Без расхода">
					<FilterSelect options={minDayOptions} value={draft.min_days} onChange={(id) => patch({ min_days: id })} />
				</FilterField>
				<FilterField label="Мин. сумма в остатке">
					<input
						type="number"
						min="0"
						className="form-control"
						style={inputStyle}
						placeholder="любая"
						value={draft.min_amount}
						onChange={(event) => patch({ min_amount: event.target.value === "" ? "" : Math.max(0, Number(event.target.value)) })}
					/>
				</FilterField>
				<FilterField label="Окно расхода">
					<FilterSelect options={usageDayOptions} value={draft.usage_days} onChange={(id) => patch({ usage_days: id })} />
				</FilterField>
			</AnalyticsFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl-4 col-md-6">
					<KpiCard title="Заморожено денег" value={summary.total_stock_value_short || "0"} sublabel={`${formatMoney(summary.items_total, 0)} позиций`} icon="uil-money-withdraw" color="#dc2626" />
				</div>
				<div className="col-xl-4 col-md-6">
					<KpiCard
						title="Без себестоимости"
						value={formatMoney(summary.no_cost_count, 0)}
						sublabel="позиций со складом без средней цены — их сумма занижена"
						icon="uil-question-circle"
						color="#6b7280"
					/>
				</div>
				<div className="col-xl-4 col-md-6">
					<KpiCard
						title="Блюда вне отчёта"
						value={formatMoney(summary.recipe_items_count, 0)}
						sublabel={`остаток по техкарте на ${summary.recipe_items_value_short || "0"} — набегает от возвратов, не деньги`}
						icon="uil-utensils"
						color="#6b7280"
					/>
				</div>
			</div>

			<StatusCards statuses={summary.statuses || []} thresholds={data?.thresholds} />

			<ThresholdsModal
				show={showThresholds}
				onClose={() => setShowThresholds(false)}
				onSaved={() => searchWithFilter(applied)}
				title="Пороги статусов запаса"
				section="frozen_funds"
				url="/analytics/settings/frozen-funds"
				fields={thresholdFields}
			/>
			<FrozenTable items={items} total={summary.items_total || 0} usageDays={data?.usage_days || applied.usage_days} hasMore={!!data?.has_more} onMore={loadMore} />
		</>
	)
}

export default FrozenFunds
